import {
	BankAccountEntityType,
	ClientEntityType,
	GuarantorEntityType,
	OrganizationEntityType,
	PatientEntityType,
} from "@/lib/supabase/supabaseTypes";
import { formatPaymentSlipData } from "@/dentlab/src/pdf-templates/pdf-template-components/qr-payment-slip/qr-payment-helpers";
import { Currency } from "@/dentlab/src/types/enums";

interface GetBankAccountForAkontoParams {
	bankAccountsLookup: Record<number, BankAccountEntityType>;
	recipient: ClientEntityType | GuarantorEntityType;
	organization: OrganizationEntityType;
}

interface GetBankAccountResult {
	bankAccount: BankAccountEntityType | null;
	error: string | null;
}

export const getBankAccountForAkonto = ({
	bankAccountsLookup,
	recipient,
	organization,
}: GetBankAccountForAkontoParams): GetBankAccountResult => {
	const bankAccountId = recipient.bank_account_id || organization.default_bank_account_id;

	if (!bankAccountId) {
		return {
			bankAccount: null,
			error: "Kein Bankkonto gefunden. Bitte hinterlegen Sie ein Standard-Bankkonto in den Labor-Einstellungen oder weisen Sie dem Empfänger ein Bankkonto zu.",
		};
	}

	const bankAccount = bankAccountsLookup[bankAccountId];
	if (!bankAccount) {
		return {
			bankAccount: null,
			error: `Das zugeordnete Bankkonto (ID: ${bankAccountId}) wurde nicht gefunden.`,
		};
	}

	return {
		bankAccount,
		error: null,
	};
};

interface ValidateAkontoDataParams {
	bankAccount: BankAccountEntityType;
	recipient: ClientEntityType | GuarantorEntityType | PatientEntityType;
}

interface ValidationResult {
	valid: boolean;
	error: string | null;
}

export const validateAkontoData = ({
	bankAccount,
	recipient,
}: ValidateAkontoDataParams): ValidationResult => {
	if (!bankAccount.qr_iban) {
		return {
			valid: false,
			error: "Das Bankkonto hat keine QR-IBAN. Bitte hinterlegen Sie eine QR-IBAN in den Bankkonto-Einstellungen.",
		};
	}

	if (!bankAccount.reference_number) {
		return {
			valid: false,
			error: "Das Bankkonto hat keine Referenznummer. Bitte hinterlegen Sie eine Referenznummer in den Bankkonto-Einstellungen (falls keine vorhanden, verwenden Sie 0000000000000).",
		};
	}

	if (!bankAccount.cdtr_name) {
		return {
			valid: false,
			error: "Das Bankkonto hat keinen Zahlungsempfänger-Namen. Bitte ergänzen Sie die Bankkontodetails.",
		};
	}

	if (!bankAccount.cdtr_street_line_1 || !bankAccount.cdtr_house_number_line_2) {
		return {
			valid: false,
			error: "Das Bankkonto hat keine vollständige Adresse. Bitte ergänzen Sie Strasse und Hausnummer in den Bankkontodetails.",
		};
	}

	if (!bankAccount.cdtr_area_code || !bankAccount.cdtr_city) {
		return {
			valid: false,
			error: "Das Bankkonto hat keine vollständige Adresse. Bitte ergänzen Sie Postleitzahl und Stadt in den Bankkontodetails.",
		};
	}

	const isClientWithCorrespondence = (recipient as ClientEntityType)?.is_correspondence_address;
	const canBeCompany = !!(recipient as ClientEntityType | GuarantorEntityType)?.is_company !== undefined;
	const isCompany = canBeCompany && (isClientWithCorrespondence
		? (recipient as ClientEntityType)?.c_is_company
		: (recipient as ClientEntityType | GuarantorEntityType)?.is_company);

	let recipientName: string;
	if (isCompany) {
		recipientName = isClientWithCorrespondence
			? (recipient as ClientEntityType)?.c_company_name || ""
			: (recipient as ClientEntityType | GuarantorEntityType)?.company_name || "";
	} else {
		const firstName = isClientWithCorrespondence
			? (recipient as ClientEntityType)?.c_first_name || ""
			: recipient.first_name || "";
		const lastName = isClientWithCorrespondence
			? (recipient as ClientEntityType)?.c_last_name || ""
			: recipient.last_name || "";
		recipientName = `${firstName} ${lastName}`.trim();
	}

	if (!recipientName) {
		return {
			valid: false,
			error: "Der Empfänger hat keinen gültigen Namen. Bitte ergänzen Sie Vor- und Nachname bzw. Firmennamen.",
		};
	}

	const postalCode = isClientWithCorrespondence
		? (recipient as ClientEntityType)?.c_postal_code || ""
		: recipient.postal_code || "";
	const city = isClientWithCorrespondence
		? (recipient as ClientEntityType)?.c_city || ""
		: recipient.city || "";

	if (!postalCode || !city) {
		return {
			valid: false,
			error: "Der Empfänger hat keine vollständige Adresse. Bitte ergänzen Sie Postleitzahl und Stadt des Empfängers.",
		};
	}

	const paymentSlipResult = formatPaymentSlipData({
		bankAccount,
		recipient,
		amount: "100.00",
		invoiceNumber: "000001",
		currency: Currency.CHF,
	});

	if (!paymentSlipResult.success) {
		return {
			valid: false,
			error: `QR-Rechnung Validierung fehlgeschlagen: ${paymentSlipResult.error}`,
		};
	}

	return {
		valid: true,
		error: null,
	};
};
